import {Injectable} from "@angular/core";
import {AddressModel, TypedAddress, addressModelFactory, typedAddressModelListFactory} from "../address/address.model";
import {paramsToQueryString} from "../http/url-params-serializer";
import {SiteDetailModel, siteDetailModelFactory, siteDetailModelListFactory} from "./site-detail.model";
import {SiteModel} from "./site.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {K2SiteModel} from "./k2-site.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Injectable()
export class SiteService {
  private url: string = "/api/sites";

  constructor(private http: HttpClient) {}

  getSites(
    page = DEFAULT_PAGE,
    limit = DEFAULT_PAGE_LIMIT,
    search: string,
    projectId: string,
  ): Promise<{rows: SiteDetailModel[], count: number}> {
    const params = {
      page: `${page}`,
      limit: `${limit}`,
      search: `${search || ""}`,
      projectId: `${projectId || ""}`,
      sequence: "true",
    };

    return this.http.get<{rows: SiteDetailModel[], count: number}>(`${this.url}`, {params})
      .pipe(map(({rows, count}) => ({rows: siteDetailModelListFactory(rows), count: +count})))
      .toPromise();
  }

  getSitesByProjectId(projectId: string): Promise<any[]> {
    return this.http.get<any[]>(`${this.url}/getlistbyproject`, {params: {projectId}})
      .toPromise();
  }

  getSiteById(id: string): Promise<SiteDetailModel> {
    return this.http.get<{rows: SiteDetailModel[]}>(`${this.url}`, {params: {id}})
      .pipe(map(json => siteDetailModelFactory(json.rows[0])))
      .toPromise();
  }

  getK2SitesByTTSiteId(siteId: string): Promise<any[]> {
    return this.http.get<{rows: any[]}>(`${this.url}/get-k2-sites`, {params: {siteId}})
      .pipe(map(json => Array.from(json["rows"] || []).filter(it => !!it)))
      .toPromise();
  }

  saveSite(site: SiteModel, selectedK2Site = undefined): Promise<boolean> {
    return this.http.post(`${this.url}/save`, {...site, selectedK2Site})
      .toPromise()
      .then(() => true);
  }

  getAddress(siteId: string, type: string): Promise<AddressModel> {
    const params = paramsToQueryString({siteId, type});
    return this.http.get<{ siteAddress: AddressModel }>(`${this.url}/address?${params}`)
      .pipe(map(({siteAddress}) => addressModelFactory(siteAddress)))
      .toPromise();
  }

  getAddresses(siteId: string, types: string[]): Promise<TypedAddress[]> {
    const params = paramsToQueryString({siteId, types});
    return this.http.get<{ type: string, address: AddressModel }[]>(`${this.url}/addresses?${params}`)
      .pipe(map((typedAddressModelListFactory)))
      .toPromise();
  }

  setAddress(siteId: string, siteAddress: AddressModel, type: string): Promise<AddressModel> {
    return this.http.post<AddressModel>(`${this.url}/address`, {siteId, siteAddress, type})
      .pipe(map(it => addressModelFactory(it)))
      .toPromise();
  }

  setAddresses(siteId: string, siteAddresses: TypedAddress[]): Promise<TypedAddress[]> {
    return this.http.post<{type: string, address: AddressModel}[]>(`${this.url}/addresses`, {siteId, siteAddresses})
      .pipe(map((typedAddressModelListFactory)))
      .toPromise();
  }

  remove(id: string): Promise<boolean> {
    return this.http.get(`${this.url}/remove`, {params: {id}})
      .toPromise()
      .then(() => true);
  }

  /**
   * Load sites from K2 (and return imported count)
   * @param {string} projectId
   */

  loadSitesFromK2ToImport(projectId: string): Promise<K2SiteModel[]> {
    return this.http.get<{sites: K2SiteModel[]}>(`${this.url}/get-k2-sites-for-import`, {params: {projectId}})
      .pipe(map(json => K2SiteModel.fromList(json.sites)))
      .toPromise();
  }

  insertK2Sites(projectId: string, sites: any[]): Promise<number> {
    return this.http.post<{count: number}>(`${this.url}/insert-k2-sites`, {projectId, sites})
      .pipe(map(json => +json.count))
      .toPromise();
  }
}
